import {
  FormFieldWidget,
  FormM2MTableFieldWidget,
  http,
  isValidatorSuccess,
  ModelFieldType,
  SPI,
  ValidatorInfo,
  ValidatorStatus,
  ViewType,
  Widget
} from '@kunlun/dependencies';
import { cloneDeep } from 'lodash-es';
import { OioMessage, OioNotification } from '@kunlun/vue-ui-antd';
import AfterSalesRefunds from './AfterSalesRefunds.vue';
import { stringifyToGraphql } from '../../../../utils/serialize';

@SPI.ClassFactory(
  FormFieldWidget.Token({
    viewType: ViewType.Form,
    ttype: ModelFieldType.ManyToMany,
    widget: 'Table',
    name: 'initialProxySaleSkus',
    model: ['deli.aries.seller.DeliAriesSellerTradeReverseOrderEdit', 'deli.aries.seller.DeliAriesSellerTradeReverseOrderCreate'],
    multi: true
  })
)
export class FormM2mMultiAfterSalesRefundsFieldWidget extends FormM2MTableFieldWidget {
  public initialize(props) {
    super.initialize(props);
    this.setComponent(AfterSalesRefunds);
    return this;
  }

  @Widget.Reactive()
  protected get allerUpdatePrice() {
    return this._formData?.allerUpdatePrice === undefined ? undefined : this._formData?.allerUpdatePrice;
  }

  @Widget.Reactive()
  get rModel() {
    return this.model;
  }

  @Widget.Reactive()
  protected get showExportAction() {
    return true;
  }

  /**
   * 用来控制表格上方的动作是否显示
   */
  @Widget.Reactive()
  protected get actionBarActions() {
    return {
      showExportAction: true,
      showImportAction: true,
      showAddRowAction: true,
      showBatchUpdateAction: true,
      showBatchDeleteAction: true,
      showPriceSourceAction: true,
      showQuickAddAction: true
    };
  }

  /**
   * 选中ERP商品编码额外的条件
   */
  @Widget.Reactive()
  protected get addRowExpendCondition() {
    return '';
  }

  /**
   * 修改包装规则是否显示提示
   */
  @Widget.Reactive()
  protected get showSpecificationTip() {
    return true;
  }

  /**
   * 「申请售后单价」是否能修改
   */
  @Widget.Reactive()
  protected get editApplyRefundUnitPrice() {
    return true;
  }

  /**
   * 修改「售后原因」字段回调
   */
  @Widget.Method()
  async changeReasonAfter() {
  }

  @Widget.Reactive()
  protected get _instance() {
    return this.getSelf();
  }

  protected getGqlQueryName() {
    const map = {
      'deli.aries.seller.DeliAriesSellerTradeReverseOrderCreate': 'deliAriesSellerTradeReverseOrderCreateQuery',
      'deli.aries.seller.DeliAriesSellerTradeReverseOrderEdit': 'deliAriesSellerTradeReverseOrderEditQuery'
    } as const;
    const name = map[this.field.model];
    if (!name) throw new Error('name not found');
    return name;
  }

  @Widget.Method()
  protected getGqlMutationName() {
    const map = {
      'deli.aries.seller.DeliAriesSellerTradeReverseOrderCreate': 'deliAriesSellerTradeReverseOrderCreateMutation',
      'deli.aries.seller.DeliAriesSellerTradeReverseOrderEdit': 'deliAriesSellerTradeReverseOrderEditMutation'
    } as const;
    const name = map[this.field.model];
    if (!name) throw new Error('name not found');
    return name;
  }

  protected iterateObjDeep(source: Record<any, any> | any[], fn: (obj: Record<any, any>) => any) {
    if (Array.isArray(source)) {
      source.forEach((item) => {
        if (Object.prototype.toString.call(item) === '[object Object]') {
          fn(item);
          Object.values(source).forEach((item2) => {
            this.iterateObjDeep(item2, fn);
          });
        }
      });
    } else if (Object.prototype.toString.call(source) === '[object Object]') {
      fn(source);
      Object.values(source).forEach((item) => {
        this.iterateObjDeep(item, fn);
      });
    }
  }

  @Widget.Method()
  async fetchData(itemDetailStr: string) {
    if (!this.formData.sellerId || !this.formData.buyerId || !this.formData.reverseType || !this.formData.salesOfficeId) {
      OioMessage.error('请将买卖家填写完整!');
      return;
    }
    if (!this.formData.buyerRelationApplyProxy || !this.formData.reverseType) {
      OioMessage.error('请将买家及售后方案信息填写完整!');
      return;
    }
    if (!this.formData.reverseDisChannel) {
      OioMessage.error('请将分销渠道填写完整!');
      return;
    }
    const reverseDisChannel = cloneDeep(this.formData.reverseDisChannel as any);
    if (reverseDisChannel) {
      delete reverseDisChannel.__draftId;
      delete reverseDisChannel.META_BIT;
      delete reverseDisChannel._d_model;
    }
    const data: Record<any, any> = {
      initialProxySaleSkus: cloneDeep(this.formData.initialProxySaleSkus as any[]).filter((item) => item.outCode),
      itemDetailStr,
      buyerRelationApplyProxy: cloneDeep(this.formData.buyerRelationApplyProxy as any),
      reverseType: this.formData.reverseType,
      sellerId: this.formData.sellerId,
      buyerId: this.formData.buyerId,
      salesOfficeId: this.formData.salesOfficeId,
      reverseDisChannel
    };

    this.iterateObjDeep(data, (obj: Record<any, any>) => {
      if (typeof obj.reverseDiscount === 'number') {
        obj.reverseDiscount = String(obj.reverseDiscount);
      }
      delete obj.__draftId;
      delete obj._X_ROW_KEY;
    });

    const gql = `
      query {
        ${this.getGqlQueryName()} {
          addItemDetailByReturnMoneyGoods(
            data: ${stringifyToGraphql(data, ['reverseType', 'priceType'])}
          ) {
            reminds
            isMerged
            refundAmount
            errMessages
            initialProxySaleSkus {
              id
              skuId
              reverseDetailId
              outCode
              itemName
              itemCode
              itemNumber
              characteristics
              priceType
              externalMaterialGroup
              minMarketPrice
              saleUnitName
              actualUnitPrice
              reverseUnitPrice
              applyRefundUnitPrice
              applyReverseNum
              reverseDiscount
              applyRefundAmount
              deliAriesItemSpecificationsProxyList {
                id
                pcsCount
                count
                unitName
                unitCode
                conversionRules
                isEnabled
              }
              reason {
                id
                code
                reason
              }
            }
          }
        }
      }    
    `;

    const res = await http.query(this.model.moduleName, gql);
    const resData = res.data[this.getGqlQueryName()].addItemDetailByReturnMoneyGoods;

    if ((resData.reminds as string[])?.length) {
      OioNotification.warning('提示', (resData.reminds as string[]).join('\n'));
    }

    return resData;
  }

  @Widget.Method()
  async updateBatch(rows: any[]) {
    if (!this.formData.sellerId || !this.formData.buyerId || !this.formData.reverseType || !this.formData.salesOfficeId) {
      OioMessage.error('请将买卖家填写完整!');
      return;
    }
    if (!this.formData.reverseDisChannel) {
      OioMessage.error('请将分销渠道填写完整!');
      return;
    }
    const reverseDisChannel = cloneDeep(this.formData.reverseDisChannel as any);
    if (reverseDisChannel) {
      delete reverseDisChannel.__draftId;
      delete reverseDisChannel.META_BIT;
      delete reverseDisChannel._d_model;
    }
    const create: Record<any, any> = {
      initialProxySaleSkus: cloneDeep(this.formData.initialProxySaleSkus as any[]).filter((item) => item.outCode),
      buyerRelationApplyProxy: cloneDeep(this.formData.buyerRelationApplyProxy as any),
      reverseType: this.formData.reverseType,
      sellerId: this.formData.sellerId,
      buyerId: this.formData.buyerId,
      salesOfficeId: this.formData.salesOfficeId,
      deliAriesSalesOrganization: cloneDeep(this.formData.deliAriesSalesOrganization as any),
      reverseDisChannel
    };

    if (this.formData.tmsOrderCode) {
      create.tmsOrderCode = this.formData.tmsOrderCode;
    }

    const copyRows = cloneDeep(rows);
    if (copyRows?.length) {
      copyRows.forEach((row) => {
        // // 不传row表示进行删除动作
        if (row) {
          if (!Object.values(row).some((item) => item !== null && item !== undefined)) {
            return;
          }
          const index = (create.initialProxySaleSkus as any[]).findIndex((i) => i.outCode === row.outCode);
          if (index !== -1) {
            create.initialProxySaleSkus.splice(index, 1);
          }
        }

        this.iterateObjDeep([create, row], (obj: Record<any, any>) => {
          if (typeof obj.reverseDiscount === 'number') {
            obj.reverseDiscount = String(obj.reverseDiscount);
          }
          delete obj.__draftId;
          delete obj._X_ROW_KEY;
        });
      });
    }

    const rst = await this.itemDetailConstructMirrorByReturnMoneyGoodsV2(create, copyRows);

    return rst;
  }

  async itemDetailConstructMirrorByReturnMoneyGoodsV2(create, copyRows) {
    const gql = `
    query {
      ${this.getGqlQueryName()} {
        itemDetailConstructMirrorByReturnMoneyGoodsV2(
          create: ${stringifyToGraphql(create, ['reverseType', 'priceType'])}
          data: ${stringifyToGraphql(copyRows || [], ['priceType'])}
        ) {
          reminds
          errMessages
          refundAmount
          initialProxySaleSkus {
            id
            skuId
            calcType
            reverseDetailId
            assemblyItemDisplay
            outCode
            itemName
            itemCode
            itemNumber
            characteristics
            priceType
            externalMaterialGroup
            minMarketPrice
            saleUnitName
            actualUnitPrice
            reverseUnitPrice
            applyRefundUnitPrice
            applyReverseNum
            reverseDiscount
            applyRefundAmount
            deliAriesItemSpecificationsProxyList {
              id
              pcsCount
              count
              unitName
              unitCode
              conversionRules
              isEnabled
            }
            reason {
            id
            code
            reason
            }
          }
        }
      }
    }
    `;
    const res = await http.query(this.model.moduleName, gql);

    const resData = res.data[this.getGqlQueryName()].itemDetailConstructMirrorByReturnMoneyGoodsV2;

    if ((resData.reminds as string[])?.length) {
      OioNotification.warning('提示', (resData.reminds as string[]).join('\n'));
    }

    return resData;
  }

  @Widget.Method()
  async confirmReturnDetail() {
    if (!this.formData.sellerId || !this.formData.buyerId || !this.formData.reverseType || !this.formData.salesOfficeId) {
      OioMessage.error('请将买卖家填写完整!');
      return;
    }

    const data: Record<any, any> = {
      id: this.formData.id,
      sellerId: this.formData.sellerId,
      buyerId: this.formData.buyerId,
      salesOfficeId: this.formData.salesOfficeId,
      reverseType: this.formData.reverseType,
      isChangeOrder: this.formData.isChangeOrder ? true : false,
      initialProxySaleSkus: cloneDeep(this.formData.initialProxySaleSkus as any[]).filter((item) => item.outCode)
    };

    if (this.formData.tmsOrderCode) {
      data.tmsOrderCode = this.formData.tmsOrderCode;
    }

    if (data.initialProxySaleSkus?.length === 0) {
      OioMessage.error('请添加商品!');
      return;
    }
    this.iterateObjDeep([data], (obj: Record<any, any>) => {
      if (typeof obj.reverseDiscount === 'number') {
        obj.reverseDiscount = String(obj.reverseDiscount);
      }
      delete obj.__draftId;
      delete obj._X_ROW_KEY;
    });
    data.initialProxySaleSkus?.forEach((item) => {
      if (item.calcType === 3) {
        item.calcType = 2;
      }
    });
    const gql = `
      query {
        ${this.getGqlQueryName()} {
          confirmReturnDetailByReturnMoneyGoods(
            data: ${stringifyToGraphql(data, ['reverseType', 'priceType'])}
          ) {
            reminds
            errMessages
            refundAmount
            refundType
            
            sourceType
            returnDetailProxySaleSkus {
            assemblyItemDisplay
              id
              skuId
              reverseDetailId
              sourcingTmsOrderDetailId
              expLabel
              outCode
              itemName
              itemCode
              itemNumber
              characteristics
              priceType
              externalMaterialGroup
              saleUnitName
              saleUnitPrice
              actualUnitPrice
              settlementPrice
              rebatePayPrice
              applyRefundUnitPrice
              reverseDiscount
              applyReverseNum
              applyRefundAmount
              policyPromotionType
              promotionSimpleCode
              deliAriesItemSpecificationsProxyList {
                id
                pcsCount
                count
                unitName
                unitCode
                conversionRules
                isEnabled
              }
              reason {
                id
                code
                reason
              }
              tradeOrderCode
              tmsOrderCode
              exWarehouseNum
              residueReturnNum
              isSecondTrace
            }
          }
        }
      }
    `;
    const res = await http.query(this.model.moduleName, gql);
    const resData: any = res.data?.[this.getGqlQueryName()]?.confirmReturnDetailByReturnMoneyGoods ?? {};

    if ((resData.reminds as string[])?.length) {
      OioNotification.warning('提示', (resData.reminds as string[]).join('\n'));
    }
    this.formData.returnDetailProxySaleSkus = resData?.returnDetailProxySaleSkus ?? [];
    resData?.refundAmount && (this.formData.refundAmount = resData.refundAmount);
    resData?.refundType && (this.formData.refundType = resData.refundType);
    resData?.sourceType && (this.formData.sourceType = resData.sourceType);

    return resData;
  }

  @Widget.Reactive()
  public setFormData(val) {
    this.change(val);
  }

  @Widget.Reactive()
  public _formData: Record<any, any> = {};

  @Widget.Watch('formData', { deep: true, immediate: true })
  public watchFormData(val) {
    this._formData = val;
  }

  @Widget.Reactive()
  protected mountedProcessFlag = false;

  protected mounted() {
    super.mounted();
    if (this.mountedCallChaining) {
      this.mountedCallChaining.hook(this.path, async () => {
        await this.mountedProcess();
      });
    } else {
      this.mountedProcess();
    }
  }

  protected async mountedProcess() {
    this.mountedProcessFlag = true;
  }

  public async validator(): Promise<ValidatorInfo> {
    const res = await this.validatorSpecific(this.value);

    if (!isValidatorSuccess(res)) {
      return res;
    }

    this.formData.initialProxySaleSkus = (this.formData.initialProxySaleSkus as any[])?.filter((item) => item.outCode) ?? [];

    (this.formData.returnDetailProxySaleSkus as any[])?.forEach((i) => {
      (this.formData.initialProxySaleSkus as any[])?.forEach((j) => {
        if (i.outCode === j.outCode) {
          i.calcType = j.calcType;
        }
      });
    });

    return {
      message: '',
      status: ValidatorStatus.Success,
      path: ''
    };
  }
}
